import { useEffect, useMemo, useRef, useState } from "react";
import { Volume2, Pause, Play, Loader2, X } from "lucide-react";
import { api, API } from "@/lib/apiClient";
import { toast } from "sonner";
import { splitSentences, buildOffsets, indexForProgress } from "@/lib/ttsSync";

/* "Read aloud" button — sends a message's text to /api/tts, plays the
 * returned MP3 inline. Supports Play / Pause / Resume / Cancel.
 *
 * While narrating, a small "Now reading" caption appears under the button
 * showing the currently-spoken sentence (estimated via char-offset → time
 * mapping). The host message bubble auto-scrolls into view so the student
 * can follow along on screen. Pausing freezes both audio and caption so they
 * can see where the narration paused. Cancel fully closes the caption and
 * restores the bubble exactly as it was before Read-aloud started.
 *
 * Singleton: only one Read-aloud audio can play at a time across the whole
 * app. Starting a new one cancels any previously-playing instance.
 */

// Module-level handle for the currently-active read-aloud instance.
let activeReader = null;

export function ReadAloudButton({ text, testId }) {
  const [state, setState] = useState("idle"); // idle | loading | playing | paused
  const [sentenceIdx, setSentenceIdx] = useState(-1);
  const audioRef = useRef(null);
  const urlRef = useRef(null);
  const buttonRef = useRef(null);

  const sentences = useMemo(() => splitSentences(text), [text]);
  const { offsets } = useMemo(() => buildOffsets(sentences), [sentences]);

  const cleanup = () => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.src = "";
      } catch (_e) { /* ignore */ }
    }
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    audioRef.current = null;
    if (activeReader && activeReader.owner === stopRef) {
      activeReader = null;
    }
  };

  const stopRef = useRef(null);
  stopRef.current = () => {
    cleanup();
    setState("idle");
    setSentenceIdx(-1);
  };

  // Stop any playing audio when the button (and its parent message/page) unmounts.
  useEffect(() => {
    return () => cleanup();
  }, []);

  const handleClick = async () => {
    // Pause currently playing audio
    if (state === "playing") {
      audioRef.current?.pause();
      setState("paused");
      return;
    }

    // Resume paused audio
    if (state === "paused" && audioRef.current) {
      try {
        await audioRef.current.play();
        setState("playing");
      } catch {
        toast.error("Couldn't resume audio");
        cleanup();
        setState("idle");
        setSentenceIdx(-1);
      }
      return;
    }

    if (state === "loading") return;
    if (!text || !text.trim()) return;

    // Cancel any other read-aloud instance already playing somewhere on the page.
    if (activeReader && activeReader.owner !== stopRef) {
      try { activeReader.stop?.(); } catch (_e) { /* ignore */ }
      activeReader = null;
    }

    setState("loading");
    setSentenceIdx(-1);

    // Safari/iOS requires audio.play() inside a user gesture — create now.
    const audio = new Audio();
    audio.preload = "auto";
    audioRef.current = audio;
    activeReader = { owner: stopRef, stop: () => stopRef.current?.() };
    try { audio.play().catch(() => {}); } catch (_e) { /* ignore */ }

    // Scroll the host message into view so the student sees where reading starts.
    try {
      buttonRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch (_e) { /* ignore */ }

    try {
      const resp = await api.post(
        "/tts",
        { text },
        { responseType: "blob", timeout: 60000 },
      );
      const url = URL.createObjectURL(resp.data);
      urlRef.current = url;
      audio.onended = () => {
        cleanup();
        setState("idle");
        setSentenceIdx(-1);
      };
      audio.onerror = () => {
        cleanup();
        setState("idle");
        setSentenceIdx(-1);
        toast.error("Couldn't play audio");
      };
      audio.ontimeupdate = () => {
        if (!audio.duration || !Number.isFinite(audio.duration)) return;
        const progress = audio.currentTime / audio.duration;
        const idx = indexForProgress(offsets, progress);
        setSentenceIdx((prev) => (prev === idx ? prev : idx));
      };
      audio.src = url;
      await audio.play();
      setState("playing");
      setSentenceIdx(0);
    } catch (e) {
      cleanup();
      setState("idle");
      setSentenceIdx(-1);
      if (!e.isSubscriptionError) {
        toast.error(e?.response?.data?.detail || "Read aloud failed");
      }
    }
  };

  const handleCancel = () => {
    stopRef.current?.();
  };

  const label =
    state === "loading"
      ? "Loading…"
      : state === "playing"
        ? "Pause"
        : state === "paused"
          ? "Resume"
          : "Read aloud";

  const title =
    state === "playing"
      ? "Pause narration"
      : state === "paused"
        ? "Resume narration"
        : "Read aloud";

  const showCaption = state !== "idle" && sentences.length > 0;
  const currentSentence =
    sentenceIdx >= 0 && sentenceIdx < sentences.length ? sentences[sentenceIdx] : "";

  return (
    <div className="inline-flex flex-col items-end gap-2 w-full">
      <div className="inline-flex items-center gap-1.5">
        <button
          ref={buttonRef}
          type="button"
          onClick={handleClick}
          data-testid={testId || "read-aloud-btn"}
          title={title}
          className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-foreground/5 hover:bg-foreground/10 text-foreground/70 hover:text-foreground transition-colors"
        >
          {state === "loading" ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : state === "playing" ? (
            <Pause className="w-3.5 h-3.5 fill-current" />
          ) : state === "paused" ? (
            <Play className="w-3.5 h-3.5 fill-current" />
          ) : (
            <Volume2 className="w-3.5 h-3.5" />
          )}
          {label}
        </button>
        {state !== "idle" && (
          <button
            type="button"
            onClick={handleCancel}
            data-testid={`${testId || "read-aloud"}-cancel`}
            title="Stop & close"
            className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-destructive/10 hover:bg-destructive/20 text-destructive transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {showCaption && (
        <div
          data-testid="read-aloud-caption"
          className="w-full max-w-full text-left rounded-xl bg-[hsl(var(--brimi-blue))]/8 border border-[hsl(var(--brimi-blue))]/30 px-3 py-2"
        >
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[hsl(var(--brimi-blue))] mb-1 flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5">
              <Volume2 className="w-3 h-3" />
              Now reading{state === "paused" ? " (paused)" : ""}
            </span>
            <span className="font-mono text-[10px] text-foreground/50">
              {sentenceIdx >= 0 ? sentenceIdx + 1 : 0}/{sentences.length}
            </span>
          </div>
          <div className="text-sm leading-snug font-medium text-foreground">
            {currentSentence || (state === "loading" ? "Loading…" : "")}
          </div>
        </div>
      )}
    </div>
  );
}

// Avoid unused warning if API is imported via re-export.
export { API };
